import React from 'react';
import styled, { css } from 'styled-components';
import { COMPONENT_MARGIN_BOTTOM } from '../../constants';
import media from '../../utils/media';
import { RichH2, RichH4, RichP1 } from '../Typography';
import { isLeftAligned } from '../../utils/variations';
import { AnimatedImageContainer } from './AnimatedImageContainer';
import { CTALink } from '../Link/CTALink';
import { getFieldValue, getParsedField } from '../../utils/helpers';
import { isFieldValid } from '../../utils/validation';
import { populateCTAGTM } from '../../utils/gtm';
const bodyTextWidth = '540px';
const Container = styled.section `
  font-family: ${({ theme }) => theme.fontFamilyBase};
  width: 100%;
  max-height: 640px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  outline: none;

  margin: 0 auto;
  background-color: ${({ theme }) => theme.primaryWhite};
  margin-bottom: ${({ hasBottomMargin }) => hasBottomMargin ? `${COMPONENT_MARGIN_BOTTOM.DESKTOP}px` : `0px`};
  overflow-x: hidden;
  overflow-y: hidden;
  ${({ alignment }) => isLeftAligned(alignment)
    ? css `
          flex-direction: row;
        `
    : css `
          flex-direction: row-reverse;
        `}
  ${media.mobile} {
    max-height: fit-content;
    flex-direction: column-reverse;
    padding-bottom: 40px;
    margin-bottom: ${({ hasBottomMargin }) => hasBottomMargin ? `${COMPONENT_MARGIN_BOTTOM.MOBILE}px` : `0px`};
  }
`;
const ContentContainer = styled.div `
  width: calc(50% - 165px);
  max-width: 540px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  *:last-child {
    margin-bottom: 0;
  }

  ${({ alignment }) => isLeftAligned(alignment)
    ? css `
          margin: 0px 0px 0px 165px;
          width: calc(50% - 165px);

          ${media.tablet} {
            margin: 0px 28px 0px 35px;
            width: calc(50% - 63px);
          }
        `
    : css `
          width: calc(50% - 200px);
          margin: 0px 165px 0px 0px;
          ${media.tablet} {
            margin: 0px 13px 0px 0px;
            width: calc(50% - 13px);
          }
        `}

  ${media.smallTablet} {
    width: calc(50% - 72px);
    margin: 0px 36px;
  }

  ${media.mobile} {
    width: calc(100% - 72px);
    margin: 0px 36px;
  }
`;
const SubTitle = styled(RichH4) `
  color: ${({ theme }) => theme.primaryRed};
  border-bottom: 1px solid ${({ theme }) => theme.greyDarkMedium};
  display: inline-block;
  padding-bottom: 5px;
  font-size: 20px;
  line-height: 25px;
  margin-bottom: 10px;
  width: fit-content;
  ${media.smallTablet} {
    font-size: 18px;
    line-height: 22px;
    margin-bottom: 14px;
  }
  ${media.mobileS} {
    margin-bottom: 11px;
  }
`;
const Title = styled(RichH2) `
  margin-bottom: 10px;
  max-width: ${bodyTextWidth};
  font-size: 65px;
  line-height: 64px;
  width: 100%;

  ${media.tablet} {
    width: 466px;
  }

  ${media.smallTablet} {
    font-size: 42px;
    line-height: 44px;
    margin-bottom: 5px;
    width: 334px;
  }

  ${media.mobileL} {
    margin-bottom: 10px;
    width: 248px;
  }
`;
const BodyText = styled(RichP1) `
  margin-bottom: 20px;
  max-width: ${bodyTextWidth};
  font-size: 18px;
  line-height: 28px;
  width: 100%;

  ${media.tablet} {
    width: 466px;
  }

  ${media.smallTablet} {
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 14px;
    width: 334px;
  }

  ${media.mobile} {
    margin-bottom: 16px;
  }

  ${media.mobileL} {
    margin-bottom: 20px;
    padding: 0;
    width: 248px;
  }
`;
export const Divider = styled.div `
  height: 100%;
  min-width: 30px;

  ${media.mobile} {
    display: none;
  }
`;
export const AnimatedContent = ({ fields }) => {
    if (!fields) {
        return null;
    }
    const { contentTitle, contentSubtitle, contentBodyText, ctaLink, ctaLinkText, ctaType, desktopBackgroundImage, mobileBackgroundImage, desktopForegroundImage, mobileForegroundImage, contentAlignment, componentBottomMargin, ariaLabel, } = fields;
    const alignment = getFieldValue(contentAlignment);
    const parsedTitle = getParsedField(contentTitle);
    const parsedSubtitle = getParsedField(contentSubtitle);
    const parsedBodyText = getParsedField(contentBodyText);
    return (React.createElement(Container, { alignment: alignment, hasBottomMargin: componentBottomMargin.value, "aria-label": ariaLabel && ariaLabel.value },
        React.createElement(ContentContainer, { alignment: alignment, className: "animated-static-content" },
            isFieldValid(contentSubtitle) && React.createElement(SubTitle, { field: parsedSubtitle }),
            isFieldValid(contentTitle) && React.createElement(Title, { field: parsedTitle }),
            isFieldValid(contentBodyText) && React.createElement(BodyText, { field: parsedBodyText }),
            isFieldValid(ctaLink, 'href') && isFieldValid(ctaLinkText) && (React.createElement(CTALink, Object.assign({ field: ctaLink, textField: ctaLinkText, typeField: ctaType }, populateCTAGTM(fields))))),
        React.createElement(Divider, null),
        isFieldValid(desktopBackgroundImage, 'src') && isFieldValid(mobileBackgroundImage, 'src') && (React.createElement(AnimatedImageContainer, { alignment: alignment, desktopBackgroundImage: desktopBackgroundImage, mobileBackgroundImage: mobileBackgroundImage, desktopForegroundImage: desktopForegroundImage, mobileForegroundImage: mobileForegroundImage }))));
};
